import { z } from 'zod';
import { WEBHOOK_EVENT_NAMES } from '../webhooks/events.js';
import { Email, IsoDateTime, Uuid, patchOf } from './common.js';
/** Module `webhooks` (docs/modules/webhooks.md, ADR 0018). Owner: task S10. */
export const WebhookEventName = z.enum(WEBHOOK_EVENT_NAMES);
export const WebhookEndpointStatus = z.enum(['active', 'paused', 'failing']);
export const WebhookEndpoint = z.object({
    id: Uuid,
    name: z.string(),
    url: z.string(),
    events: z.array(WebhookEventName),
    siteIds: z.array(Uuid).nullable(),
    skipSources: z.array(z.string()),
    /** Module that manages this endpoint (e.g. 'omp-link'); such endpoints are read-only on the Webhooks page. */
    ownerModule: z.string().nullable(),
    /** Send oldest first and wait for retries before newer deliveries. */
    ordered: z.boolean(),
    payloadProfile: z.enum(['standard', 'minimal']),
    status: WebhookEndpointStatus,
    failingSince: IsoDateTime.nullable(),
    alertAfterMinutes: z.number().int(),
    alertEmail: z.string().nullable(),
    lastSuccessAt: IsoDateTime.nullable(),
    /** Last 4 characters only. */
    secretHint: z.string(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
export const WebhookEndpointCreate = z.object({
    name: z.string().trim().min(1).max(100),
    url: z.url().max(2000),
    events: z.array(WebhookEventName).max(50).default([]),
    siteIds: z.array(Uuid).max(500).nullable().default(null),
    skipSources: z.array(z.string().max(40)).max(10).default([]),
    ordered: z.boolean().default(false),
    payloadProfile: z.enum(['standard', 'minimal']).default('standard'),
    alertAfterMinutes: z.number().int().min(0).max(10080).default(60),
    alertEmail: Email.nullable().default(null),
});
export const WebhookEndpointUpdate = patchOf(WebhookEndpointCreate).extend({ status: z.enum(['active', 'paused']).optional() });
/** Returned once, on create and on rotate. */
export const WebhookEndpointWithSecret = z.object({ endpoint: WebhookEndpoint, secret: z.string() });
export const WebhookDeliveryStatus = z.enum(['queued', 'sending', 'retrying', 'succeeded', 'abandoned']);
export const WebhookAttempt = z.object({
    attempt: z.number().int(),
    at: IsoDateTime,
    statusCode: z.number().int().nullable(),
    /** e.g. webhook.error.timeout, webhook.error.refused_address */
    errorKey: z.string().nullable(),
    durationMs: z.number().int(),
    /** First 500 characters of the response body. */
    responseExcerpt: z.string().nullable(),
});
export const WebhookDelivery = z.object({
    id: Uuid,
    endpointId: Uuid,
    type: WebhookEventName,
    status: WebhookDeliveryStatus,
    attempts: z.number().int(),
    nextAttemptAt: IsoDateTime.nullable(),
    occurredAt: IsoDateTime,
    createdAt: IsoDateTime,
    completedAt: IsoDateTime.nullable(),
    lastStatusCode: z.number().int().nullable(),
});
export const WebhookDeliveryDetail = WebhookDelivery.extend({
    /** null once cleared (succeeded deliveries after 7 days). */
    payload: z.unknown().nullable(),
    attemptLog: z.array(WebhookAttempt),
});
export const WebhookDeliveryQuery = z.object({
    endpointId: Uuid.optional(),
    status: WebhookDeliveryStatus.optional(),
    type: WebhookEventName.optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
/** The JSON body of every webhook (ADR 0018). */
export const WebhookEnvelope = z.object({
    id: Uuid,
    type: WebhookEventName,
    apiVersion: z.string(),
    occurredAt: IsoDateTime,
    createdAt: IsoDateTime,
    tenantId: Uuid,
    source: z.string(),
    externalEventId: z.string().nullable(),
    data: z.unknown(),
});
export const WebhooksTenantSettings = z.object({
    logRetentionDays: z.number().int().min(1).max(365).default(30),
});
export const WebhooksPlatformSettings = z.object({
    /** Minutes after the first try for each retry (ADR 0018). */
    retryScheduleMinutes: z.array(z.number().int().min(1).max(10080)).min(1).max(20).default([1, 5, 15, 30, 60, 120, 240, 480, 720, 1440]),
    /** Hosts allowed over plain http (test server, mock OMP). */
    allowHttpHosts: z.array(z.string().max(253)).max(20).default([]),
    /** Private addresses or CIDR ranges that may be called (LAN test server). Empty = none. */
    allowPrivateTargets: z.array(z.string().max(50)).max(20).default([]),
});
//# sourceMappingURL=webhooks.js.map